import moment from 'moment';
import { Vue, Component } from 'vue-property-decorator';

import APIService from '../../../services/APIService';
import ValidationService from '../../../services/ValidationService';
import GradeRequest from '../../../models/requests/GradeRequest';
import SubjectResponse from '../../../models/responses/SubjectResponse';
import GradeResponse from '../../../models/responses/GradeResponse';
import Validation from '../../../models/Validation';
import ValidationObject from '../../../models/ValidationObject';

/**
 * Grade Form to create and edit a grade.
 */
@Component({
  name: 'GradeForm',
})
export default class GradeForm extends Vue {
  private gradeRequest: GradeRequest = {
    name: null,
    grade: null,
    weight: 1,
    classAverage: null,
    date: moment(new Date()).format('DD.MM.YYYY').toString(),
    canceled: false,
  };

  private gradeValidation: { [key: string]: Validation } = {
    name: { type: 'name', isValid: false, message: null } as Validation,
    grade: { type: 'grade', isValid: false, message: null } as Validation,
    weight: { type: 'weight', isValid: false, message: null } as Validation,
    classAverage: {
      type: 'grade',
      isValid: false,
      message: null,
      optional: true,
    } as Validation,
    date: { type: 'date', isValid: false, message: null } as Validation,
  };

  get editGrade(): GradeResponse | null {
    return this.$store.state.editGrade;
  }

  get selectedSubject(): SubjectResponse | null {
    return this.$store.state.selectedSubject;
  }

  get validate(): (
    type: string,
    value: unknown,
    optional?: boolean,
  ) => Validation {
    return ValidationService.validate;
  }

  get validateObject(): (
    validations: ValidationObject,
    object: { [key: string]: unknown },
  ) => ValidationObject {
    return ValidationService.validateObject;
  }

  get isObjectValid(): (form: { [key: string]: Validation }) => boolean {
    return ValidationService.isObjectValid;
  }

  get title(): string {
    if (this.editGrade) return 'forms.gradeForm.editTitle';
    return 'forms.gradeForm.title';
  }

  /**
   * Checks if the form should be in edit mode and if yes, fill in the fields.
   */
  async mounted() {
    if (this.editGrade) {
      this.gradeRequest = {
        ...this.editGrade,
        date: moment(this.editGrade.date, 'YYYY-MM-DD').format('DD.MM.YYYY'),
      };
    }
    this.validateObject(this.gradeValidation, this.gradeRequest);

    await setTimeout(() => {
      (this.$refs.nameInput as HTMLSdxInputElement).doFocus();
    }, 200);
  }

  /**
   * Updates the grade request with new properties
   *
   * @remarks
   * Casts certain properties to the correct format.
   *
   * @param form - object containing new properties to add to the grade request
   */
  private setForm(form: GradeRequest): void {
    if (!form) return;
    if (form.grade) form.grade = Number(form.grade) || null;
    if (form.weight) form.weight = Number(form.weight) || null;
    if (form.classAverage)
      form.classAverage = Number(form.classAverage) || null;

    this.gradeRequest = { ...this.gradeRequest, ...form };

    this.validateObject(this.gradeValidation, this.gradeRequest);
  }

  /**
   * Toggles grade form popup.
   */
  private toggleGradeForm(): void {
    this.$store.commit('toggleGradeForm');
  }

  /**
   * Saves grade via APIService.
   *
   * @remarks
   * If the edit mode is active, the id is set. If not, no id is set.
   */
  private async saveGrade(): Promise<void> {
    const formattedRequest = {
      ...this.gradeRequest,
      date: moment(this.gradeRequest.date, 'DD.MM.YYYY').format('YYYY-MM-DD'),
    };
    if (this.editGrade) {
      await APIService.updateGrade(this.editGrade.id, formattedRequest);
    } else if (this.selectedSubject) {
      await APIService.createGrade(this.selectedSubject.id, formattedRequest);
    }
    this.$emit('reload');
    this.toggleGradeForm();
  }

  /**
   * Checks if the form is valid and if so, calls the save function.
   */
  private async submitWhenValid() {
    if (!this.isObjectValid(this.gradeValidation)) return;
    this.saveGrade();
  }
}
